/**
 * GhostChordSuggestion
 *
 * One ghost candidate after the last chord (Fieldbook §6): a true per-function
 * silhouette drawn with the SAME path generator ChordShape uses for real
 * chords (generateShapePath, exported for this reuse) — dashed ink stroke,
 * NO fill (dashed = not yet real, §3), with a handwriting-voice label beneath
 * (candidate.label, e.g. "what the ear expects"). Hover auditions the
 * candidate; click commits it.
 *
 * Borrowed convention (decision 9e866a99, 2026-07-23): borrowed chords keep
 * the silhouette of their FUNCTION everywhere — shape from scaleDegree, plum
 * wash + "bor" badge when committed, small tag on the ghost. Ghosts match the
 * canvas so a committed ghost keeps its shape.
 */
import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import type { ChordQuality, ScaleDegree } from '@/types/chord';
import type { GhostCandidate } from '@/lib/ghosts';
import { generateShapePath } from './ChordShape';
import { DEFAULT_CHORD_SIZE } from '@/utils/constants';
import styles from './GhostChordSuggestion.module.css';

interface GhostChordSuggestionProps {
  candidate: GhostCandidate;
  index: number;
  zoom: number;
  isHovered: boolean;
  onHover: () => void;
  onLeave: () => void;
  onClick: () => void;
}

const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII'];

function romanFor(scaleDegree: ScaleDegree, quality: ChordQuality): string {
  const base = ROMAN[scaleDegree] ?? '';
  const q = String(quality);
  const isMinorish =
    q === 'minor' || q.startsWith('min') || q === 'diminished' || q.startsWith('dim') || q === 'halfdim7';
  let roman = isMinorish ? base.toLowerCase() : base;
  if (q === 'diminished' || q.startsWith('dim') || q === 'halfdim7') roman += '°';
  return roman;
}

/** Convention B: silhouette always follows function (scaleDegree), borrowed
 * or not — borrowed-ness is carried by the tag, matching the main canvas. */
function shapePathFor(candidate: GhostCandidate, size: number): string {
  return generateShapePath(candidate.chord.scaleDegree, size);
}

const GhostChordSuggestionComponent: React.FC<GhostChordSuggestionProps> = ({
  candidate,
  index,
  zoom,
  isHovered,
  onHover,
  onLeave,
  onClick,
}) => {
  const reduceMotion = useReducedMotion();
  const size = DEFAULT_CHORD_SIZE * zoom;
  const roman = romanFor(candidate.chord.scaleDegree, candidate.chord.quality);
  const shapePath = shapePathFor(candidate, size);
  const labelFontSize = Math.min(16, Math.max(12, size * 0.28));

  // Prominence from weight; a touch brighter on hover.
  const baseOpacity = 0.4 + candidate.weight * 0.2; // 0.4–0.6
  const opacity = isHovered ? 0.9 : baseOpacity;

  return (
    <motion.div
      className={styles.ghostContainer}
      initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.9, y: 8 }}
      animate={
        reduceMotion
          ? { opacity }
          : { opacity, scale: isHovered ? 1.04 : 1, y: 0 }
      }
      exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.9, y: -8 }}
      transition={
        reduceMotion
          ? { duration: 0.12 }
          : { duration: 0.2, ease: [0.4, 0.0, 0.2, 1], delay: index * 0.05 }
      }
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onClick={onClick}
      role="button"
      aria-label={`Add ${candidate.label}`}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <svg
        className={styles.ghostShape}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
      >
        {/* Dashed ink outline, no fill — dashed = not yet real (§3). */}
        <path d={shapePath} fill="none" stroke="var(--ink)" strokeWidth={1.5} strokeDasharray="4 3" />
        {candidate.chord.isChromatic && (
          <g transform={`translate(${size * 0.78}, ${size * 0.12})`}>
            <rect x={-13} y={-8} width={26} height={15} rx={3} fill="var(--wash-borrowed)" opacity={0.85} />
            <text textAnchor="middle" y={4} className={styles.ghostTag}>
              bor
            </text>
          </g>
        )}
        <text
          x={size / 2}
          y={size / 2}
          textAnchor="middle"
          dominantBaseline="central"
          className={styles.ghostNumeral}
          style={{ fontSize: labelFontSize }}
        >
          {roman}
        </text>
      </svg>
      <span className={styles.ghostLabel}>{candidate.label}</span>
    </motion.div>
  );
};

GhostChordSuggestionComponent.displayName = 'GhostChordSuggestion';

export const GhostChordSuggestion = React.memo(GhostChordSuggestionComponent);
